import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import SectionHead from "@/components/SectionHead";
import ProcessLoop from "@/components/ProcessLoop";
import FinalCTA from "@/components/FinalCTA";
import { APPLYKAR_URL } from "@/lib/constants";
import ScrollRail from "@/components/ScrollRail";
import { LoopRail } from "@/components/Rails";
import LottiePlayer from "@/components/LottiePlayer";
import cycleAnim from "@/assets/lottie/process-cycle.json";

const MODEL = [
  ["One accountable owner", "Systems, visibility and hiring are run as a single programme with a single point of responsibility, instead of being split across vendors."],
  ["Senior oversight", "Executive leadership stays involved in delivery and optimisation, not only in the first conversation."],
  ["Selective roster", "A limited number of engagements at a time, so attention is not diluted."],
  ["Long-term operating relationship", "The aim is a standing operating layer that improves over time, not a one-off deliverable."],
];

const Hero = () => {
  return (
    <section className="hp-hero-light hp-immersive" data-testid="hww-hero" style={{ minHeight: "70vh" }}>
      <img 
        src="/images/hww_strategy_1791101726606.jpg" 
        className="hp-immersive-img"
        style={{ objectPosition: 'center 40%' }}
        alt="Strategy session" 
      />
      
      {/* Light gradient for text readability */}
      <div
        className="absolute inset-0 pointer-events-none bg-gradient-to-b lg:bg-gradient-to-r from-white via-white/95 to-white/10"
        aria-hidden="true"
      />
      <div className="hop-container relative z-10 py-16 md:py-24 grid lg:grid-cols-12 gap-10 items-center w-full">
        <div className="lg:col-span-8">
          <Reveal>
            <p className="hp-label text-[var(--hop-brand)]">How we work</p>
            <h1 className="hp-h1 mt-5">An operating layer, not a project queue.</h1>
            <p className="hp-lead mt-7" style={{ color: "#3c4768" }}>
              House of Projects works as a business consultancy. We research the business, diagnose what is constraining it,
              execute across systems, visibility and talent, and then optimise using what the work teaches us.
            </p>
          </Reveal>
        </div>
        <div className="hidden lg:block lg:col-span-4">
          <LottiePlayer className="hp-lottie-hero drop-shadow-xl opacity-90 mix-blend-multiply" data={cycleAnim} label="Four-stage operating loop" />
        </div>
      </div>
      <div className="hp-rule absolute bottom-0 left-0 right-0 w-full" aria-hidden="true" />
    </section>
  );
};

const Loop = () => (
  <section className="hp-section relative overflow-hidden bg-[#f7f7f5]" aria-labelledby="hww-loop">
    {/* Background Image with strong readable gradient fade */}
    <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 0 }} aria-hidden="true">
      <img
        src="/images/hww_strategy_1791101726606.jpg"
        alt=""
        className="w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-b lg:bg-gradient-to-r from-[#f7f7f5] via-[#f7f7f5]/90 to-[#f7f7f5]/10" />
    </div>

    <div className="hop-container relative" style={{ zIndex: 10 }}>
      <SectionHead label="The operating loop" title={<span id="hww-loop">Research, Diagnose, Execute, Optimise.</span>}
        lead="Every engagement follows the same four stages, whichever capability does the delivery." />
      <div className="mt-14 bg-white/60 backdrop-blur-md p-8 md:p-12 rounded-2xl border border-slate-200/60 shadow-sm">
        <ScrollRail rail={(p) => <LoopRail p={p} />}><ProcessLoop stacked /></ScrollRail>
      </div>
    </div>
  </section>
);

const Model = () => (
  <section className="hp-section hp-tint" aria-labelledby="hww-model">
    <div className="hop-container grid lg:grid-cols-12 gap-14">
      <div className="lg:col-span-4">
        <SectionHead label="Engagement model" title={<span id="hww-model">How the relationship is structured.</span>} />
      </div>
      <div className="lg:col-span-8">
        <Reveal>
          <dl className="divide-y divide-slate-200 border-y border-slate-200">
            {MODEL.map(([t, d]) => (
              <div key={t} className="py-6 grid sm:grid-cols-[15rem_1fr] gap-1 sm:gap-6">
                <dt className="font-display text-xl font-semibold text-hop-blue">{t}</dt>
                <dd className="hp-body text-[15px]">{d}</dd>
              </div>
            ))}
          </dl>
        </Reveal>
      </div>
    </div>
  </section>
);

const Capabilities = () => (
  <section className="hp-section" aria-labelledby="hww-caps">
    <div className="hop-container">
      <SectionHead label="Where execution happens" title={<span id="hww-caps">Three capabilities, deployed against the diagnosis.</span>}
        lead="Which capability is used, and in what order, depends on what the research finds." />
      <Reveal>
        <ul className="mt-10 grid md:grid-cols-3 gap-6">
          {[
            ["hop", "House of Projects", "Platforms, systems and consulting", "/what-we-do"],
            ["ae", "Arise & Echo", "Social media and visibility infrastructure", "/arise-and-echo"],
          ].map(([k, n, d, to]) => (
            <li key={k} data-arm={k} className="hp-card">
              <div className="hp-arm-bar h-1.5 rounded-t-md" />
              <div className="p-6">
                <h3 className="hp-h3">{n}</h3>
                <p className="hp-body mt-2 text-[15px]">{d}</p>
                <Link to={to} className="hp-link hp-arm-accent mt-5">Explore <ArrowRight className="arrow w-4 h-4" aria-hidden="true" /></Link>
              </div>
            </li>
          ))}
          <li data-arm="ak" className="border border-slate-200 rounded-md bg-white">
            <div className="hp-arm-bar h-1.5 rounded-t-md" />
            <div className="p-6">
              <h3 className="hp-h3">Applykar</h3>
              <p className="hp-body mt-2 text-[15px]">Hiring and talent acquisition infrastructure, run as its own platform.</p>
              <a href={APPLYKAR_URL} target="_blank" rel="noopener noreferrer" className="hp-link hp-arm-accent mt-5"
                 aria-label="Explore Applykar (opens applykar.in in a new tab)">Explore</a>
            </div>
          </li>
        </ul>
      </Reveal>
    </div>
  </section>
);

export default function HowWeWork() {
  return (
    <>
      <Seo title="How We Work"
        description="House of Projects operates as a business consultancy: research, diagnose, execute and optimise, under one accountable owner with senior oversight."
        path="/how-we-work" />
      <Hero /><Loop /><Model /><Capabilities />
      <FinalCTA />
    </>
  );
}
